<template>
  <div class="nebTreeNode">
    <div class="nebTreeNode-labelWrapper">
      <span :class="[
          'nebTreeNode-label',
          { isClickable, hasChildren, isSelected, selectable }
        ]"
        @click="onNodeClick"
      >
        {{ node.label }}
      </span>

      <div class="nebTreeNode-labelWrapper-right">
        <span class="nebTreeNode-rightLabel"
          v-if="hasRightLabel"
          :class="{ isClickable, hasChildren, isSelected }"
        >
          {{ node.rightLabel.toLocaleString() }}
        </span>

        <neb-icon v-if="hasChildren && !open"
          name="caretRight"
          @click.native="onOpenClick"
        ></neb-icon>

        <neb-icon v-if="hasChildren && open"
          name="caretDown"
          @click.native="onOpenClick"
        ></neb-icon>
      </div>
    </div>

    <transition name="drop">
      <div :class="[
          'nebTreeNode-children',
          { open }
        ]"
        v-if="open && hasChildren"
      >
        <tree-node v-for="n in node.children"
          :key="n.value"
          :node="n"
          :selected="selected"
          :selectable="selectable"
          :singleSelect="singleSelect"
          @nodeClick="n => $emit('nodeClick', n)"
        ></tree-node>
      </div>
    </transition>
  </div>
</template>

<script>
import { hasIn, isNull, isUndefined, isEmpty, includes } from 'lodash'
import NebIcon from '../../Icon'

export default {
  name: 'TreeNode',

  components: {
    NebIcon
  },

  props: {
    node: {
      type: Object,
      required: true
    },
    selected: {
      type: [Array, String],
      default: () => []
    },
    selectable: {
      type: Boolean,
      default: false
    },
    singleSelect: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    open: false
  }),

  computed: {
    hasChildren () {
      return hasIn(this.node, 'children') &&
        !isNull(this.node.children) &&
        !isUndefined(this.node.children) &&
        !isEmpty(this.node.children)
    },
    isClickable () {
      return this.node.clickable !== undefined
        ? this.node.clickable
        : this.node.clickable || this.selectable
    },
    isSelected () {
      return this.singleSelect
        ? this.selected === this.node.value
        : includes(this.selected, this.node.value)
    },
    hasRightLabel () {
      return hasIn(this.node, 'rightLabel') &&
        !isNull(this.node.rightLabel) &&
        !isUndefined(this.node.rightLabel)
    }
  },

  methods: {
    onOpenClick () { this.open = !this.open },
    onNodeClick () {
      if (this.node.clickable === false) { return }

      if (this.node.clickable || this.selectable) {
        this.$emit('nodeClick', this.node)
      }
    }
  }
}
</script>

<style lang="scss">
@import'../../style.scss';

.nebTreeNode {
  box-sizing: border-box;
  overflow: hidden;

  &:not(:last-child) {
    padding-bottom: 15px;
  }

  &-children {
    box-sizing: border-box;
    overflow: hidden;
    padding: 15px 0 0 10px;

    &.open {
      padding-bottom: 0;
    }
  }

  &-label,
  &-rightLabel {
    color: $slate-grey;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    font-family: $defaultFont;
    font-size: 12px;
    display: flex;
    align-items: center;

    &.isClickable,
    &.selectable {
      cursor: pointer;
    }

    &.selectable {
      &:not(.isClickable) {
        cursor: default;
      }
    }

    &.hasChildren {
      font-weight: bold;
    }

    &.isSelected {
      color: $tiffany-blue;
    }
  }

  &-rightLabel {
    margin-right: 10px;
    max-width: 50%;

    &:not(.hasChildren) {
      margin-right: 22px;
    }
  }

  &-label {
    max-width: 70%;
  }

  &-labelWrapper {
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    justify-content: space-between;

    .nebIcon {
      cursor: pointer;
    }

    &-right {
      display: flex;
    }
  }

  .drop-enter,
  .drop-leave-to {
    height: 0;
    padding: 0 0 0 10px;
    opacity: 0;
  }

  .drop-enter-to {
    height: auto;
    opacity: 1;
    padding: 15px 0 0 10px;
  }

  .drop-leave {
    height: auto;
    opacity: 1;
    padding: 0 0 0 0;
  }

  .drop-enter-active,
  .drop-leave-active {
    transition: padding 0.2s ease-in-out, opacity 0.2s ease-in-out;
  }
}
</style>
